import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { BaseComponent } from '@core/base.component';
import { Invite } from '@models/businesses';
import { BusinessInviteService } from '@repos/businesses/users/business-invite.service';
import { AuthenticationService } from '@services/authentication/authentication.service';
import { NavigationService } from '@services/nav/nav.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { AuthDispatch } from '@state/auth/auth.dispatch';
import { filter, first, map } from 'rxjs/operators';
import { BusinessesSignUpRequestsRepo } from '@repos/signupRequests/signUpRequests.repo';
import { Store } from '@ngxs/store';
import { AuthState } from '@state/auth';
import { Logger } from '@core/logger';
import { BusinessSignupRequestStatutes } from '@models/businessSignUpRequests';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import { firstValueFrom } from 'rxjs';

interface VM {
	invites: RadioGroupItem<Invite>[];
	selectedInvite: Invite;
}

@Component({
	selector: 'app-pending-invite',
	templateUrl: './pending-invite.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class PendingInviteComponent
	extends BaseComponent<VM>
	implements OnInit
{
	constructor(
		private authenticationService: AuthenticationService,
		private authDispatch: AuthDispatch,
		private navigationService: NavigationService,
		private businessInviteService: BusinessInviteService,
		private snackbarService: SnackbarService,
		private businessesSignUpRequestsRepo: BusinessesSignUpRequestsRepo,
		private store: Store,
	) {
		super({ invites: [], selectedInvite: null });
	}

	ngOnInit(): void {
		this.authenticationService
			.getInvites()
			.pipe(
				filter((invites) => !!invites),
				map((invites) =>
					invites.map(
						(invite) =>
							({
								label: `${invite.businessName} invited by ${invite.createdByName}`,
								value: invite,
							}) as RadioGroupItem<Invite>,
					),
				),
				first(),
			)
			.subscribe(
				(invites) => {
					this.VM = {
						invites,
						selectedInvite: invites.length === 1 ? invites[0].value : null,
					};
				},
				(error) => {
					Logger.exception(error);
					this.authDispatch.SetUserWithAuth();
				},
			);
	}

	async acceptInvite() {
		try {
			if (!this.VM.selectedInvite) {
				this.snackbarService.openSnackBar({
					text: 'Please select an invite to accept or select Create New',
					type: 'error',
				});
			}
			await firstValueFrom(
				this.authenticationService.acceptInvite({
					inviteId: this.VM.selectedInvite.id,
				}),
			);
			this.VM.invites
				.filter(({ value }) => value.id !== this.VM.selectedInvite.id)
				.forEach(({ value }) =>
					this.businessInviteService.Delete(value.id, value.businessId),
				); // Clean up any other invites until multiple business support is added
			this.navigationService.goToCreateUser();
		} catch (error) {
			Logger.exception(error);
			this.snackbarService.openSnackBar({
				text: `Failed to accept invite, please try again. If error persists please contact support`,
				type: 'error',
			});
			this.authDispatch.LogOut();
		}
	}

	createNewBusiness() {
		this.businessesSignUpRequestsRepo
			.GetRequestsByEmailAndStatus(
				this.store.selectSnapshot(AuthState.getEmail),
				BusinessSignupRequestStatutes.APPROVED,
			)
			.pipe(first())
			.subscribe((requests) => {
				if (requests?.length > 0) {
					this.authDispatch.SetSignUpRequest(requests[0]);
					this.navigationService.goToCreateUser();
				} else {
					this.navigationService.goToSignUp();
				}
			});
	}
}
